import { useCallback, useState } from 'react';
import { VALID_STATE } from 'src/constants/validate';
import Icon from './Icon';

type Props = {
  name: string;
  className?: string;
  placeholder?: string;
  onChangeValue: (val: string) => void;
  validate?: Record<string, (value: string) => boolean>;
  errors?: Record<string, string>;
  value: string;
  list: any[];
  component: (data: any) => JSX.Element;
};

type ReturnType = {
  CustomSelectBox: JSX.Element;
  value: string;
  isValid: boolean;
  setValue: (value: string) => void;
};

export default function SelectBox({ name, value, onChangeValue, placeholder, list, className, component }: Props): JSX.Element {
  return (
    <div className="relative flex items-center justify-end">
      <select
        data-value={value}
        name={name}
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        className={
          [
            // 'absolute',
            'w-full',
            'appearance-none',
            'border-ice-blue',
            'w-full',
            'px-4',
            'py-3.5',
            'border',
            'border-ice-blue',
            'rounded-sm',
            'bg-white',
          ].join(' ') + (className ? ' ' + className : '')
        }
      >
        <option disabled hidden value="" className="text-brown-grey">
          {placeholder}
        </option>
        {list.map((data) => {
          return component(data);
        })}
      </select>
      <Icon name="down" size="20" className="absolute right-4 pointer-events-none"></Icon>
    </div>
  );
}

export function useSelectBox(options?: Partial<Props>): ReturnType {
  const [value, setValue] = useState('');
  const [validState, setValidState] = useState(VALID_STATE.NONE);
  const [, setErrorMessage] = useState('');
  const { validate, errors } = options as Props;

  const onValidate = useCallback(
    (val: string) => {
      if (validate) {
        const result = Object.entries(validate).some(([key, fn]) => {
          if (!fn(val)) {
            setValidState(VALID_STATE.ERROR);
            errors && errors[key] && setErrorMessage(errors[key]);
            return true;
          }
          return false;
        });
        if (!result) {
          setValidState(VALID_STATE.PASS);
          setErrorMessage('');
        }
      }
    },
    [validate, setErrorMessage, setValidState],
  );

  const onChangeValue = useCallback((val: string) => {
    setValue(val);
    onValidate(val);
  }, []);

  return {
    value,
    isValid: validState === VALID_STATE.PASS,
    setValue: onChangeValue,
    CustomSelectBox: (
      <SelectBox
        {...{
          name: '',
          value,
          onChangeValue,
          list: [],
          component: function component() {
            return <></>;
          },
          ...(options || {}),
        }}
      ></SelectBox>
    ),
  };
}
